// Shared QBR helpers used by Hub + Cycle Detail + Program Detail.

// Status strings that mean "this ticket / opportunity is closed."
// Autotask's ticket status comes through as "Complete" (5) when refreshed
// from the API. The vault import preserved free-text strings like
// "Complete", "Completed", "Waiting for Delivery", "Won", "Lost", etc.
// A row is closed only if its status is one of the canonically-closed
// values; anything else (or empty) is treated as open.
const CLOSED_PATTERNS = /^(complete|completed|closed|won|lost|closed-won|closed-lost|resolved)$/i;

export function isRowClosed(row) {
  if (!row) return false;
  const s = `${row.status || ''}`.trim();
  if (!s) return false;
  return CLOSED_PATTERNS.test(s);
}

export function countRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const closed = list.filter(isRowClosed).length;
  return { total: list.length, closed, open: list.length - closed };
}

// Days from today to a YYYY-MM-DD date string. Positive = future, negative =
// past, null = unknown. Returns whole days (rounded).
export function daysUntil(ymd) {
  if (!ymd) return null;
  const t = new Date(ymd + 'T00:00:00');
  if (isNaN(t.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((t - today) / (1000 * 60 * 60 * 24));
}

// Human-friendly relative day phrase: "in 4 days", "today", "2 days overdue".
export function relativeDayPhrase(ymd) {
  const d = daysUntil(ymd);
  if (d === null) return '—';
  if (d === 0)    return 'today';
  if (d === 1)    return 'tomorrow';
  if (d === -1)   return '1 day overdue';
  if (d > 1)      return `in ${d} days`;
  return `${Math.abs(d)} days overdue`;
}

// Phase display: human label + MUI color. Mirrors the PHASES list in
// cmapy/routes/cma/qbrcycle/_tools.py. Keep in sync.
const PHASE_META = {
  'audit-pending':         { label: 'Audit pending',       color: 'default' },
  'audit-in-progress':     { label: 'Audit in progress',   color: 'info'    },
  'pre-meeting-cleanup':   { label: 'Pre-meeting cleanup', color: 'warning' },
  'ready-to-meet':         { label: 'Ready to meet',       color: 'success' },
  'meeting-scheduled':     { label: 'Meeting scheduled',   color: 'info'    },
  'post-meeting-followup': { label: 'Post-meeting work',   color: 'warning' },
  'closed':                { label: 'Closed',              color: 'success' },
};

export function phaseLabel(phase) {
  const m = PHASE_META[phase];
  return m ? m.label : (phase || '—');
}

export function phaseColor(phase) {
  const m = PHASE_META[phase];
  return m ? m.color : 'default';
}

// Days since a date string (ISO-ish — both YYYY-MM-DD and full ISO timestamps
// from Autotask work). Returns null if the input is missing/unparseable.
export function daysSince(when) {
  if (!when) return null;
  const t = new Date(when);
  if (isNaN(t.getTime())) return null;
  return Math.floor((Date.now() - t.getTime()) / (1000 * 60 * 60 * 24));
}

// Stale = open AND has gone N+ days without any Autotask activity. The refresh
// runbook stamps last_activity_date onto each row when it runs.
// Default threshold: 30 days.
export const STALE_THRESHOLD_DAYS = 30;

export function rowStaleness(row) {
  // Returns { stale: bool, days: number|null }
  if (!row || isRowClosed(row)) return { stale: false, days: null };
  const d = daysSince(row.last_activity_date);
  if (d === null) return { stale: false, days: null };
  return { stale: d >= STALE_THRESHOLD_DAYS, days: d };
}

// Best-effort "how long has this row been waiting?" in days. Used to sort
// Outstanding tables stalest-first. Falls back to days since the cycle's
// meeting_date when last_activity_date hasn't been populated yet (i.e. the
// refresh runbook hasn't run, or the row predates that feature).
export function rowWaitDays(row) {
  if (!row) return 0;
  const fromActivity = daysSince(row.last_activity_date);
  if (fromActivity !== null) return fromActivity;
  const fromMeeting = daysSince(row.meeting_date);
  if (fromMeeting !== null && fromMeeting > 0) return fromMeeting;
  return 0;
}
